import React, { useState } from 'react';
import { Facebook, Instagram, Youtube, Mail, Send, CheckCircle, Play, X } from 'lucide-react';

const Links = () => {
  const [showContact, setShowContact] = useState(false);
  const [showreelOpen, setShowreelOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: 'Linktree Contact',
          message: formData.message
        })
      });
      
      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => {
          setSubmitSuccess(false);
          setShowContact(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center px-4 py-8 max-w-md mx-auto">
      {/* Profile Section */}
      <div className="text-center mb-8">
        <div className="relative inline-block mb-4">
          <div className="absolute -inset-1 bg-gradient-to-r from-magenta to-pink-500 rounded-full blur opacity-50"></div>
          <img
            src="https://customer-assets.emergentagent.com/job_0c54bed9-ef3a-492f-9870-d5af335d368c/artifacts/9rs7f7do_Untitled%20design-43.png"
            alt="Ryan Daniels"
            className="relative w-28 h-28 rounded-full object-cover border-2 border-magenta"
          />
        </div>
        <h1 className="text-2xl font-bold text-white mb-1">Ryan Daniels</h1>
        <p className="text-magenta text-sm font-medium">Mentalist & Comedy Magician</p>
        <p className="text-gray-400 text-xs mt-1">Mind Reading • Real Reactions</p>
      </div>

      {/* Showreel Button */}
      <button
        onClick={() => setShowreelOpen(true)}
        className="w-full mb-2 py-4 px-6 bg-magenta rounded-xl text-white font-semibold text-center flex items-center justify-center gap-2 hover:bg-magenta/90 transition-colors shadow-lg shadow-magenta/30"
        data-testid="links-watch-showreel-btn"
      >
        <Play size={20} fill="currentColor" />
        Watch Showreel
      </button>

      {/* Main Site Button */}
      <a
        href="/"
        className="w-full mb-2 py-4 px-6 bg-neutral-800 border border-magenta/30 rounded-xl text-white font-semibold text-center flex items-center justify-center gap-2 hover:bg-neutral-700 transition-colors"
        data-testid="links-main-site-btn"
      >
        Main Site
      </a>

      {/* Corporate Button */}
      <a
        href="/corporate"
        className="w-full mb-4 py-4 px-6 bg-neutral-800 border border-magenta/30 rounded-xl text-white font-semibold text-center flex items-center justify-center gap-2 hover:bg-neutral-700 transition-colors"
        data-testid="links-corporate-btn"
      >
        Corporate
      </a>

      {/* Contact Button */}
      <button
        onClick={() => setShowContact(!showContact)}
        className="w-full mb-2 py-4 px-6 bg-neutral-800 border border-magenta/30 rounded-xl text-white font-semibold text-center flex items-center justify-center gap-2 hover:bg-neutral-700 transition-colors"
      >
        <Mail size={20} className="text-magenta" />
        Contact Me
      </button>

      {/* Email Link */}
      <a
        href="mailto:ryan@ryandanielsmentalist.com"
        className="mb-4 text-magenta hover:text-white transition-colors text-sm"
      >
        ryan@ryandanielsmentalist.com
      </a>

      {/* Contact Form */}
      {showContact && (
        <div className="w-full mb-4 p-4 bg-neutral-900 border border-white/10 rounded-xl animate-in slide-in-from-top-2">
          {submitSuccess ? (
            <div className="flex flex-col items-center py-6 text-center">
              <CheckCircle size={48} className="text-green-500 mb-3" />
              <p className="text-white font-medium">Message Sent!</p>
              <p className="text-gray-400 text-sm">I'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-magenta text-sm"
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-magenta text-sm"
              />
              <textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={3}
                className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-magenta text-sm resize-none"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-magenta rounded-lg text-white font-semibold flex items-center justify-center gap-2 hover:bg-magenta/90 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Sending...' : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Logo Link to Website */}
      <a
        href="/"
        className="mb-4 hover:opacity-80 transition-opacity"
      >
        <img
          src="https://customer-assets.emergentagent.com/job_2307e085-4eaf-4338-88bf-6170046d3d85/artifacts/mjuj40fa_Ryan%20daniels%20mentalist-4.png"
          alt="Ryan Daniels - Visit Website"
          className="h-40 w-auto"
        />
      </a>

      {/* Social Links - Same style as main site */}
      <div className="flex items-center justify-center gap-6 mb-8">
        <a 
          href="https://www.facebook.com/ryandanielsmentalist"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 transition-opacity duration-300"
          aria-label="Facebook"
          style={{ color: 'var(--color-magenta)' }}
        >
          <Facebook size={32} />
        </a>
        <a 
          href="https://www.instagram.com/mentalistryand?igsh=MTlxZnJhZ3AzZjl5dA%3D%3D&utm_source=qr"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 transition-opacity duration-300"
          aria-label="Instagram"
          style={{ color: 'var(--color-magenta)' }}
        >
          <Instagram size={32} />
        </a>
        <a 
          href="https://youtube.com/@ryandanielsmagic?si=XvAmTYceoInryCyg"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 transition-opacity duration-300"
          aria-label="YouTube"
          style={{ color: 'var(--color-magenta)' }}
        >
          <Youtube size={32} />
        </a>
        <a 
          href="https://www.tiktok.com/@ryandmagic?_r=1&_t=ZT-938A3IBjVaI"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 transition-opacity duration-300"
          aria-label="TikTok"
          style={{ color: 'var(--color-magenta)' }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
          </svg>
        </a>
      </div>

      {/* Footer */}
      <div className="text-center mt-auto pt-4">
        <p className="text-gray-600 text-xs">© 2026 Ryan Daniels</p>
        <a
          href="https://app.emergent.sh/?utm_source=emergent-badge"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-2 text-gray-500 text-xs hover:text-gray-400 transition-colors"
        >
          <img
            src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4"
            alt="Emergent"
            className="w-4 h-4"
          />
          Made with Emergent
        </a>
      </div>

      {/* Showreel Modal */}
      {showreelOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm px-4"
          onClick={() => setShowreelOpen(false)}
          data-testid="links-showreel-modal"
        >
          <div
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowreelOpen(false)}
              className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors"
              data-testid="links-showreel-close-btn"
              aria-label="Close showreel"
            >
              <X size={28} />
            </button>
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl bg-neutral-900">
              <video
                autoPlay
                controls
                playsInline
                className="w-full h-full"
                data-testid="links-showreel-video"
              >
                <source src={`${process.env.REACT_APP_BACKEND_URL}/api/static/promo.mp4`} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Links;
